import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Descriptions,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Tag,
  Switch,
  Tooltip,
  Alert,
  Spin,
  Space,
  Row,
  Col,
  Statistic,
  Input,
  Steps,
  Divider,
} from "antd";
import {
  RocketOutlined,
  CreditCardOutlined,
  TeamOutlined,
  AppstoreOutlined,
  UserOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  SyncOutlined,
  SafetyCertificateOutlined,
  FileProtectOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  LockOutlined,
  SearchOutlined,
  CalendarOutlined,
  WalletOutlined,
  LinkOutlined,
  CopyOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAttorneyTMCounts,
  fetchPlansByAttorneyType,
  fetchBanks,
} from "../../../services/orderService";
import { usePlanFeatures } from "../../../hooks/useUserProfile";
import TmCountSuggestion from "../../createCustomerOrder/TmCountSuggestion";
import { API_ELSTIC_URL, API_BASE_URL } from "../../../../config";
import { planTypeLabel } from "../../../utils/planType";
import { useGstRate } from "../../../hooks/useTaxRates";
import axios from "axios";
import { authHeaders } from "../../../utils/session";

const { Option } = Select;

/* No rate constant here. GST comes from the tax master through useGstRate() —
   a rate written into the code keeps being charged after the real one changes,
   and this one lands on a customer's invoice. */

// Attorney row statuses
const S = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const WIZARD_STEPS = [
  { title: "Plan", icon: <CreditCardOutlined /> },
  { title: "Attorneys", icon: <TeamOutlined /> },
  { title: "Features", icon: <AppstoreOutlined /> },
  { title: "Billing", icon: <CalculatorOutlined /> },
  { title: "Review", icon: <CheckCircleOutlined /> },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseAttorneyResponse = (response) => {
  const data = response?.data ?? {};
  const numbers = (data.attorney_numbers ?? []).map(String);
  const details = data.details ?? [];
  const countMap = {};
  details.forEach((d) => {
    countMap[String(d.attorney_number)] = d.count ?? 0;
  });
  return {
    attorneys: numbers.map((num) => ({
      attorney_number: num,
      tm_count: countMap[num] ?? 0,
    })),
    total_count: data.total_count ?? 0,
  };
};

const getFeatureValue = (plan, featureKey) => {
  const f = plan?.features?.find((feat) => feat.feature_key === featureKey);
  if (!f) return null;
  return f.feature_type === "numeric"
    ? parseInt(f.default_value, 10)
    : f.default_value === "true";
};

const fmt = (n, decimals = 0) =>
  Number(n ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const round2 = (n) => Math.round((n ?? 0) * 100) / 100;

/**
 * A price, exactly as it was given.
 *
 * The decimal places come from the value itself, not from a rule chosen here: a
 * stored "1.00" prints as ₹1.00 and "0.0010" as ₹0.0010. Anything fixed —
 * two places, four places — is this screen deciding what the price is, and it
 * showed a real ₹0.001 per-mark rate as ₹0.00.
 *
 * Only digit grouping is added. That needs the API to send the price as a
 * string; a JSON number has already lost its trailing zeros before it arrives.
 */
const price = (n) => {
  if (n === null || n === undefined || n === "") return "0";
  const raw = String(n).trim();
  const value = Number(raw);
  // Not a number at all — show whatever it is rather than a silent zero.
  if (!Number.isFinite(value)) return raw;
  const dot = raw.indexOf(".");
  const decimals = dot === -1 ? 0 : Math.min(raw.length - dot - 1, 20);
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// InputNumber money formatting. Group the integer part only — applying
// /\B(?=(\d{3})+(?!\d))/g to the whole string also groups the decimals
// ("1234.5678" → "1,234.5,678").
const groupAmount = (value) => {
  const [intPart = "", decPart] = `${value ?? ""}`.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
};

const parseAmount = (value) => `${value ?? ""}`.replace(/₹\s?|,/g, "");

// Parse "1,2,5,6,16,20" → [1, 2, 5, 6, 16, 20]
const parseFeatureIds = (str) =>
  (str ?? "")
    .toString()
    .split(",")
    .map((id) => parseInt(id.trim(), 10))
    .filter(Boolean);

// Seed featureStates from allFeatures + existing IDs user already has.
const buildFeatureStates = (featureList, existingIds) => {
  const idSet = new Set(existingIds);
  const seenKeys = new Set();
  const states = {};
  const sorted = [...featureList].sort(
    (a, b) => (a.feature_order ?? 0) - (b.feature_order ?? 0),
  );
  sorted.forEach((f) => {
    const owned = idSet.has(f.id);
    if (owned && !seenKeys.has(f.feature_key)) {
      states[`id_${f.id}`] = true;
      seenKeys.add(f.feature_key);
    } else {
      states[`id_${f.id}`] = false;
    }
  });
  return states;
};

// =============================================================================
const SubscriptionDetails = ({
  subscriptionData,
  allFeatures = [],
  onUpdateSubscription,
  isUpdating,
  rawAttorneyNumbers = [],
}) => {
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // ── Extend Free Trial ─────────────────────────────────────────────────────
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendDate, setExtendDate] = useState(null);
  const [extendReason, setExtendReason] = useState("");
  const [isExtending, setIsExtending] = useState(false);

  const isFreeTrialPlan =
    ["free_trial", "free trial"].includes(
      (subscriptionData?.plan_type ?? "").toLowerCase().trim()
    );

  const handleExtendTrial = async () => {
    if (!extendDate) {
      toast.warning("Please select a new expiry date.");
      return;
    }
    if (!extendReason.trim()) {
      toast.warning("Please enter an extension reason.");
      return;
    }
    setIsExtending(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/subscription/extend-subscription-expiry-date`,
        {
          user_id: subscriptionData?.user_id,
          new_expiry_date: extendDate.format("YYYY-MM-DD"),
          extended_by: 1,
          extension_reason: extendReason.trim(),
        },
        { headers: { ...authHeaders() } }
      );
      // API returns just { message: "..." } on success — axios only resolves for 2xx
      toast.success(res.data?.message || "Free trial extended successfully!");
      setExtendModalOpen(false);
      setExtendDate(null);
      setExtendReason("");
      // Refetch subscription data so the updated expiry date shows immediately
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails"] });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to extend free trial.");
    } finally {
      setIsExtending(false);
    }
  };

  // ── Step 0: Plan ─────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [originalPlanId, setOriginalPlanId] = useState(null); // track if plan changed
  const [planType, setPlanType] = useState("monthly");
  const [planPrice, setPlanPrice] = useState(0);
  /* The start date is display-only and lives on the form. It used to be the base
     the new expiry was counted from, which is what made a renewal land a month
     after the term began rather than a month after it ends. */
  const [endDate, setEndDate] = useState(null);

  /**
   * Where the new cycle ends.
   *
   * The server writes this date verbatim — it does not extend anything itself —
   * so whatever goes in the box is the subscription's real expiry. Sending the
   * date already on the record therefore renews a subscription into the past,
   * and the customer pays and is expired the same moment.
   *
   * Counted from today, not from the current expiry.
   *
   * The old term's unused days are not carried over here — the server refunds
   * them as money, through `proration_credit` on the billing preview. Extending
   * from the old expiry as well would hand the customer the same remainder
   * twice: once as a discount and once as extra days. So a monthly upgrade
   * bought today ends a month from today, whatever the old term had left.
   */
  const CYCLE = {
    monthly: [1, "month"],
    quarterly: [3, "month"],
    annual: [1, "year"],
  };
  const nextCycleEnd = (type) => {
    const [amount, unit] = CYCLE[type] || CYCLE.monthly;
    return dayjs().startOf("day").add(amount, unit);
  };

  // ── Step 1: Attorneys ─────────────────────────────────────────────────────
  // Existing attorneys are LOCKED (read-only, no delete)
  // Empty slots are editable (check status, but no delete — they are plan-defined)
  const [existingAttorneys, setExistingAttorneys] = useState([]);
  // Each empty slot: { value, status, message, tmCount, isChecking }
  const [emptySlots, setEmptySlots] = useState([]);
  const [totalTMCount, setTotalTMCount] = useState(0);
  const [manualTMCount, setManualTMCount] = useState(null);

  /* The per-TM price while it is being typed. Held apart from the committed one
     so a half-finished number is never what the subscription gets billed at;
     `null` means nothing is being edited. */
  const [priceDraft, setPriceDraft] = useState(null);

  /* The live GST rate, from the tax master. */
  const gst = useGstRate();

  const [autoFetchingAttorneys, setAutoFetchingAttorneys] = useState(false);
  const [wizardPlanLimit, setWizardPlanLimit] = useState(0);
  const [attorneyNameOptions, setAttorneyNameOptions] = useState({}); // { [num]: [{attorney_name}] }
  const [selectedAttorneyNames, setSelectedAttorneyNames] = useState({}); // { [num]: string[] }

  // Auto-select when only one name is available
  useEffect(() => {
    Object.entries(attorneyNameOptions).forEach(([num, names]) => {
      if (names.length === 1) {
        setSelectedAttorneyNames((prev) => {
          if (prev[num]?.length > 0) return prev;
          return { ...prev, [num]: [names[0].attorney_name] };
        });
      }
    });
  }, [attorneyNameOptions]);

  // ── Step 2: Features ──────────────────────────────────────────────────────
  const [featureStates, setFeatureStates] = useState({});

  // ── Step 3: Billing ───────────────────────────────────────────────────────
  const [calcTMCount, setCalcTMCount] = useState(0);
  const [calcPlanPrice, setCalcPlanPrice] = useState(0);
  // Negotiated pre-GST total the admin is aiming for. null = follow the computed
  // Base Amount, so the helper stays quiet unless the price is actually inexact.
  const [targetTotal, setTargetTotal] = useState(null);
  const [includeGST, setIncludeGST] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [utrNo, setUtrNo] = useState("");
  const [paymentAttachment, setPaymentAttachment] = useState(null);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [upgradePending, setUpgradePending] = useState(false);

  // ── Billing preview (proration + wallet) ─────────────────────────────────
  const [billingPreview, setBillingPreview] = useState(null);
  const [billingPreviewLoading, setBillingPreviewLoading] = useState(false);

  // ── Online payment link state ─────────────────────────────────────────────
  const [paymentLink, setPaymentLink] = useState(null);
  const [onlineOrderId, setOnlineOrderId] = useState(null);
  const [paymentLinkPaid, setPaymentLinkPaid] = useState(false);

  // Polling interval ref — cleared on unmount
  const pollRef = useRef(null);

  // ─── Bank list from API ───────────────────────────────────────────────────
  const { data: bankData = [], isLoading: banksLoading } = useQuery({
    queryKey: ["bank-master-list"],
    queryFn: fetchBanks,
    staleTime: 1000 * 60 * 60,
  });

  // ─── Fetch plan-specific features when plan changes ──────────────────────
  const planChanged =
    selectedPlanId !== null &&
    originalPlanId !== null &&
    selectedPlanId !== originalPlanId;

  const { data: planFeatures = [], isLoading: isLoadingPlanFeatures } =
    usePlanFeatures(planChanged ? selectedPlanId : null);

  // ─── Derived ─────────────────────────────────────────────────────────────
  const isAttorney = subscriptionData?.is_for_attorney ?? 1;
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;
  const effectiveTM = manualTMCount !== null ? manualTMCount : totalTMCount;

  const existingFeatureIds = parseFeatureIds(subscriptionData?.features);

  // wizardFeatures: if plan changed → plan-specific features; otherwise → allFeatures
  const wizardFeatures =
    planChanged && planFeatures.length > 0 ? planFeatures : allFeatures;

  // Combined attorney list for TM calculation and review
  const allVerifiedAttorneys = [
    ...existingAttorneys.filter((a) => a.status === S.SUCCESS),
    ...emptySlots.filter((s) => s.status === "valid" && s.value.trim()),
  ];

  const verifiedExistingCount = existingAttorneys.filter(
    (a) => a.status === S.SUCCESS,
  ).length;
  const verifiedNewCount = emptySlots.filter(
    (s) => s.status === "valid" && s.value.trim(),
  ).length;
  const totalVerifiedCount = verifiedExistingCount + verifiedNewCount;

  const pendingExisting = existingAttorneys.filter(
    (a) => a.attorney_number.trim() && a.status !== S.SUCCESS,
  );
  const pendingNew = emptySlots.filter(
    (s) => s.value.trim() && s.status !== "valid",
  );
  const totalPendingCount = pendingExisting.length + pendingNew.length;

  // Use wizardFeatures (plan-specific or allFeatures) for the feature list
  const sortedFeatures = [...wizardFeatures].sort(
    (a, b) => (a.feature_order ?? 0) - (b.feature_order ?? 0),
  );

  const enabledCount = sortedFeatures.filter((f) => {
    const val = featureStates[`id_${f.id}`];
    // If plan changed, only count toggled-on features (no fallback to existing)
    if (planChanged) {
      return val !== undefined ? !!val : false;
    }
    // If plan NOT changed, fall back to existing user features
    return val !== undefined ? !!val : existingFeatureIds.includes(f.id);
  }).length;

  // Billing calculation
  const isFreeTrialType = planType === "free_trial";
  const baseAmount = isFreeTrialType ? 0 : (calcTMCount ?? 0) * (calcPlanPrice ?? 0);
  const gstAmount = isFreeTrialType ? 0 : (includeGST ? baseAmount * gst.fraction : 0);
  const totalAmount = baseAmount + gstAmount;

  // Live billing deductions — proration_credit + wallet_balance are fixed per plan (fetched once).
  // wallet_deduction and netAmountToPay recompute on every TM count / price / GST change.
  const prorationCredit = billingPreview?.proration_credit ?? 0;
  const walletBalance = billingPreview?.wallet_balance ?? 0;
  const afterProration = Math.max(round2(totalAmount - prorationCredit), 0);
  const liveWalletDeduction = round2(Math.min(walletBalance, afterProration));
  const netAmountToPay = Math.max(0, round2(afterProration - liveWalletDeduction));

  /* An edit in progress: the draft exists and differs from what is committed.
     Typing a value and typing it back again leaves nothing to save. */
  const priceDirty = priceDraft !== null && priceDraft !== calcPlanPrice;

  const savePlanPrice = () => {
    if (!priceDirty) { setPriceDraft(null); return; }
    setCalcPlanPrice(priceDraft);
    setPriceDraft(null);
    toast.success(`Plan amount set to ₹${price(priceDraft)} per TM`);
  };

  // ─── Init empty slots helper ─────────────────────────────────────────────
  const initEmptySlots = (count) =>
    Array.from({ length: count }, () => ({
      value: "",
      status: null, // null | "valid" | "invalid"
      message: "",
      tmCount: 0,
      isChecking: false,
    }));

  // ─── Recalculate TM total from existing + new verified ───────────────────
  const recalcTotalTM = (existList, slotList) => {
    const existSum = existList
      .filter((a) => a.status === S.SUCCESS)
      .reduce((s, a) => s + (a.tm_count || 0), 0);
    const slotSum = slotList
      .filter((s) => s.status === "valid")
      .reduce((s, slot) => s + (slot.tmCount || 0), 0);
    const total = existSum + slotSum;
    setTotalTMCount(total);
    if (manualTMCount === null) setCalcTMCount(total);
  };

  // ─── Calculate plan limit and rebuild slots ──────────────────────────────
  const rebuildAttorneySlots = (plan, existingNums) => {
    const limit = getFeatureValue(plan, "number_of_attorney") || 0;
    setWizardPlanLimit(limit);

    const existCount = existingNums.length;

    if (limit < existCount) {
      // Plan downgrade: existing attorneys exceed new limit
      // Reset completely — show only empty slots for the new limit
      setExistingAttorneys([]);
      setEmptySlots(initEmptySlots(limit));
      setTotalTMCount(0);
      if (manualTMCount === null) setCalcTMCount(0);
      toast.warning(
        `Plan allows only ${limit} attorney(s). Existing ${existCount} attorney(s) removed — please re-add up to ${limit}.`,
      );
    } else {
      // Plan allows same or more: keep existing, add empty slots for remainder
      const emptyCount = Math.max(0, limit - existCount);
      setEmptySlots(initEmptySlots(emptyCount));
    }
  };

  // ─── Load plans ───────────────────────────────────────────────────────────
  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await fetchPlansByAttorneyType(isAttorney);
      setPlans(res.data ?? []);
    } catch {
      toast.error("Failed to load plans.");
    } finally {
      setPlansLoading(false);
    }
  };

  const applyPricing = (plan, type) => {
    if (!plan) return;
    /* A new plan or term brings its own price, so an unsaved draft against the
       old one has nothing left to apply to. */
    setPriceDraft(null);
    if (type === "free_trial") {
      setPlanPrice(0);
      setCalcPlanPrice(0);
      return;
    }
    const price =
      type === "annual"
        ? parseFloat(plan.annual_price ?? 0)
        : parseFloat(plan.monthly_price ?? 0);
    setPlanPrice(price);
    setCalcPlanPrice(price);
  };

  // ─── Open wizard ──────────────────────────────────────────────────────────
  const openWizard = () => {
    const pid = subscriptionData?.plan_id ?? null;
    const rawType = (subscriptionData?.plan_type ?? "monthly")
      .toLowerCase()
      .replace(/\s+/g, "_");
    const type = rawType.startsWith("annual") ? "annual" : rawType;
    const price = parseFloat(subscriptionData?.plan_price ?? 0);

    setSelectedPlanId(pid);
    setOriginalPlanId(pid); // track the original plan for change detection
    setPlanType(type);
    setPlanPrice(price);
    setCalcPlanPrice(price);

    const parsedStart = subscriptionData?.subscription_start_date
      ? dayjs(subscriptionData.subscription_start_date, "DD/MM/YYYY")
      : null;
    /* The new term's expiry, not the one being replaced. Opening the wizard with
       the current expiry in the box is what let a renewal go out dated in the
       past whenever the admin never touched the plan type. */
    const parsedEnd = nextCycleEnd(type);

    setEndDate(parsedEnd);

    form.setFieldsValue({
      plan_id: pid,
      plan_type: type,
      subscription_start_date: parsedStart,
      subscription_end_date: parsedEnd,
    });

    // ── Attorneys: from subscriptionData.attorney_numbers ──────────────
    const nums = Array.isArray(subscriptionData?.attorney_numbers)
      ? subscriptionData.attorney_numbers
      : (subscriptionData?.attorney_numbers ?? "")
          .toString()
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean);

    const initExisting = nums.map((num, i) => ({
      key: i,
      attorney_number: String(num),
      tm_count: 0,
      status: S.IDLE,
    }));
    setExistingAttorneys(initExisting);

    // Pre-fill attorney name options + selections from rawAttorneyNumbers
    const namesLookup = {};
    const preSelected = {};
    rawAttorneyNumbers.forEach((item) => {
      const key = String(item.attorney_number);
      if (item.attorney_names?.length) {
        namesLookup[key] = item.attorney_names.map((n) =>
          typeof n === "string" ? { attorney_name: n } : n,
        );
        preSelected[key] = item.attorney_names.map((n) =>
          typeof n === "string" ? n : n.attorney_name,
        );
      }
    });
    setAttorneyNameOptions(namesLookup);
    setSelectedAttorneyNames(preSelected);

    // Empty slots will be set once plans load and we know the limit
    setEmptySlots([]);
    setWizardPlanLimit(0);

    const initTotal = subscriptionData?.total_tm_count ?? 0;
    setTotalTMCount(initTotal);
    setCalcTMCount(initTotal);
    setManualTMCount(null);

    setIncludeGST(
      subscriptionData?.gst_inclusion === 1 ||
        subscriptionData?.gst_inclusion === true,
    );

    const rawPS = subscriptionData?.payment_status;
    setPaymentStatus(
      rawPS === 1 || rawPS === "paid"
        ? "paid"
        : rawPS === 0 || rawPS === "unpaid"
          ? "unpaid"
          : null,
    );
    setPaymentMode(subscriptionData?.payment_mode ?? null);
    setSelectedBank(subscriptionData?.bank_name ?? null);
    setUtrNo(subscriptionData?.utr_no ?? "");
    setPaymentAttachment(null);
    setPaymentErrors({});

    if (allFeatures.length > 0) {
      setFeatureStates(buildFeatureStates(allFeatures, existingFeatureIds));
    } else {
      setFeatureStates({});
    }

    setCurrentStep(0);
    loadPlans();
    setWizardOpen(true);
  };

  // ── Once plans load, set up attorney slots based on current plan ─────────
  useEffect(() => {
    if (wizardOpen && plans.length > 0 && selectedPlanId) {
      const plan = plans.find((p) => p.id === selectedPlanId);
      if (plan && wizardPlanLimit === 0) {
        const existNums = existingAttorneys.map((a) => a.attorney_number);
        rebuildAttorneySlots(plan, existNums);
      }
    }
  }, [plans, selectedPlanId, wizardOpen]);

  // Re-seed features when allFeatures loads (for original plan / no plan change)
  useEffect(() => {
    if (
      wizardOpen &&
      !planChanged &&
      allFeatures.length > 0 &&
      Object.keys(featureStates).length === 0
    ) {
      setFeatureStates(buildFeatureStates(allFeatures, existingFeatureIds));
    }
  }, [allFeatures, wizardOpen, planChanged]);

  // Seed features when plan-specific features load (plan was changed)
  useEffect(() => {
    if (wizardOpen && planChanged && planFeatures.length > 0 && selectedPlan) {
      // Enable only the plan's default features, not all features
      const planDefaultIds = (selectedPlan.features ?? []).map((f) => f.id);
      setFeatureStates(buildFeatureStates(planFeatures, planDefaultIds));
    }
  }, [planFeatures, wizardOpen, planChanged, selectedPlan]);

  // ── Stop polling on unmount ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── On mount: restore pending online payment link from localStorage ──────
  useEffect(() => {
    const uid = subscriptionData?.user_id;
    if (!uid) return;
    const stored = localStorage.getItem(`online_payment_${uid}`);
    if (!stored) return;
    let parsed;
    try { parsed = JSON.parse(stored); } catch { localStorage.removeItem(`online_payment_${uid}`); return; }
    const { payment_link: link, order_id: oid } = parsed;
    if (!link || !oid) { localStorage.removeItem(`online_payment_${uid}`); return; }
    axios.get(`${API_BASE_URL}/subscription/get-order-details`, {
      params: { order_id: oid, user_id: uid },
      headers: { ...authHeaders() },
    }).then((res) => {
      const status = String(res.data?.data?.payment_status);
      if (status === '2') {
        setPaymentLink(link);
        setOnlineOrderId(oid);
        setPaymentLinkPaid(false);
        startPaymentPolling(oid, uid);
      } else {
        localStorage.removeItem(`online_payment_${uid}`);
      }
    }).catch(() => {
      localStorage.removeItem(`online_payment_${uid}`);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData?.user_id]);

  // ── Billing preview: fetch fixed proration_credit + wallet_balance once per plan ─
  // total_amount is intentionally omitted — backend returns the same fixed values
  // regardless of amount. All live computation (wallet_deduction, net) is done in the frontend.
  const fetchBillingPreview = async () => {
    if (!selectedPlanId || !subscriptionData?.user_id) return;
    setBillingPreviewLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/subscription/calculate-upgrade-billing`,
        {
          user_id: subscriptionData.user_id,
          plan_id: selectedPlanId,
          plan_type: planType,
        },
        { headers: { ...authHeaders() } },
      );
      if (res.data?.success) {
        setBillingPreview(res.data.data);
      }
    } catch {
      // Non-fatal — billing preview is informational only
    } finally {
      setBillingPreviewLoading(false);
    }
  };

  // ── Payment polling helpers ────────────────────────────────────────────────
  const stopPaymentPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPaymentPolling = (orderId, userId) => {
    stopPaymentPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/subscription/get-order-details`, {
          params: { order_id: orderId, user_id: userId },
          headers: { ...authHeaders() },
        });
        if (String(res.data?.data?.payment_status) === '1') {
          stopPaymentPolling();
          setPaymentLinkPaid(true);
          setPaymentLink(null);
          localStorage.removeItem(`online_payment_${userId}`);
          queryClient.invalidateQueries({ queryKey: ['subscriptionDetails'] });
          toast.success("Payment received! Subscription is now active.");
        }
      } catch {
        // ignore polling errors silently
      }
    }, 10000);
  };

  // const isFreeTrial = ["free_trial", "free trial"].includes(
  //   (subscriptionData?.plan_type ?? "").toLowerCase().replace(/\s+/g, " "),
  // );

  // ─── Plan change handler ─────────────────────────────────────────────────
  const handlePlanChange = (planId) => {
    setSelectedPlanId(planId);
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      applyPricing(plan, planType);
      form.setFieldsValue({ plan_type: planType });

      // ── Features: reset states based on whether plan changed ──────
      if (planId === originalPlanId) {
        // Switching back to original plan → restore user's existing features
        if (allFeatures.length > 0) {
          setFeatureStates(buildFeatureStates(allFeatures, existingFeatureIds));
        }
      } else {
        // New plan selected → enable only the plan's default features
        const planDefaultIds = (plan.features ?? []).map((f) => f.id);
        setFeatureStates(buildFeatureStates(allFeatures, planDefaultIds));
      }

      // ── Rebuild attorney slots based on new plan's limit ──────────
      const existNums = existingAttorneys.map((a) => a.attorney_number);
      rebuildAttorneySlots(plan, existNums);

      // Reset manual TM override
      setManualTMCount(null);
    }
  };

  const handlePlanTypeChange = (type) => {
    setPlanType(type);
    applyPricing(selectedPlan, type);
    const newEnd = nextCycleEnd(type);
    setEndDate(newEnd);
    form.setFieldsValue({ subscription_end_date: newEnd });
  };

  // ─── Empty slot handlers (same pattern as AttorneyDetails.jsx) ───────────
  const patchSlot = (index, patch) => {
    setEmptySlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const handleSlotInputChange = (index, value) => {
    patchSlot(index, { value, status: null, message: "", tmCount: 0 });
  };

  const handleCheckSlotStatus = async (index) => {
    const num = emptySlots[index].value.trim();
    if (!num) {
      toast.warning("Please enter an attorney number first.");
      return;
    }

    patchSlot(index, { isChecking: true, status: null, message: "" });

    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/attorney/get-attorney-status-count?attorney_number=${num}`,
      );

      const { status, data } = res.data;

      if (status === 200 || status === 409) {
        const validAttorneys = data?.valid_attorneys || [];
        const details = data?.details || [];
        const detail = details.find((d) => d.attorney_number === num);

        if (validAttorneys.includes(num) && detail?.exists) {
          const count = Number(detail.count || 0);
          patchSlot(index, {
            status: "valid",
            message: `Valid — ${count} TMs`,
            tmCount: count,
            isChecking: false,
          });
          if (detail?.attorney_names?.length) {
            setAttorneyNameOptions((prev) => ({
              ...prev,
              [num]: detail.attorney_names.map((n) =>
                typeof n === "string" ? { attorney_name: n } : n,
              ),
            }));
          }
          toast.success(`Attorney ${num} verified successfully.`);
        } else {
          patchSlot(index, {
            status: "invalid",
            message: "Invalid Attorney Number",
            tmCount: 0,
            isChecking: false,
          });
          toast.warning(`Attorney ${num} is invalid.`);
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
        const errBody = err.response.data;
        const errMsg = errBody?.message || "";
        const eData = errBody?.data || {};
        const validAttorneys = eData?.valid_attorneys || [];
        const details = eData?.details || [];

        if (details.length === 0) {
          patchSlot(index, {
            status: "invalid",
            message: errMsg || "Attorney Number already assigned",
            tmCount: 0,
            isChecking: false,
          });
          toast.error(errMsg || "Attorney Number already assigned");
          return;
        }

        const detail = details.find((d) => d.attorney_number === num);
        if (validAttorneys.includes(num) && detail?.exists) {
          const count = Number(detail.count || 0);
          patchSlot(index, {
            status: "valid",
            message: `Valid — ${count} TMs`,
            tmCount: count,
            isChecking: false,
          });
          if (detail?.attorney_names?.length) {
            setAttorneyNameOptions((prev) => ({
              ...prev,
              [num]: detail.attorney_names.map((n) =>
                typeof n === "string" ? { attorney_name: n } : n,
              ),
            }));
          }
        } else {
          patchSlot(index, {
            status: "invalid",
            message: "Invalid Attorney Number",
            tmCount: 0,
            isChecking: false,
          });
          toast.warning(errMsg || "Attorney number is invalid.");
        }
      } else {
        patchSlot(index, { isChecking: false });
        toast.error(
          err.response?.data?.message || "Failed to check attorney status.",
        );
      }
    }

    // After status check, recalculate totals
    setTimeout(() => {
      setEmptySlots((currentSlots) => {
        recalcTotalTM(existingAttorneys, currentSlots);
        return currentSlots;
      });
    }, 100);
  };

  // ─── Auto-fetch existing attorney TM counts ──────────────────────────────
  const fetchAndGoToAttorneys = async () => {
    // Corporates skip the Attorneys step entirely
    if (!isAttorney) {
      setCurrentStep(2);
      return;
    }

    const existingNums = existingAttorneys
      .filter((a) => a.attorney_number.trim() !== "")
      .map((a) => a.attorney_number.trim());

    if (existingNums.length === 0) {
      setCurrentStep(1);
      return;
    }

    setAutoFetchingAttorneys(true);
    setExistingAttorneys((prev) =>
      prev.map((a) =>
        a.attorney_number.trim() !== ""
          ? { ...a, status: S.LOADING, tm_count: 0 }
          : a,
      ),
    );

    try {
      const response = await fetchAttorneyTMCounts(existingNums);
      const { attorneys: result } = parseAttorneyResponse(response);
      const countMap = {};
      result.forEach((a) => {
        countMap[String(a.attorney_number)] = a.tm_count ?? 0;
      });

      // Extract attorney_names if returned by this endpoint
      const nameOptionsMap = {};
      ((response?.data ?? {}).details || []).forEach((d) => {
        if (d.attorney_names?.length) {
          nameOptionsMap[String(d.attorney_number)] = d.attorney_names.map((n) =>
            typeof n === "string" ? { attorney_name: n } : n,
          );
        }
      });
      if (Object.keys(nameOptionsMap).length > 0) {
        setAttorneyNameOptions((prev) => ({ ...prev, ...nameOptionsMap }));
      }

      setExistingAttorneys((prev) => {
        const updated = prev.map((a) => {
          if (a.attorney_number.trim() !== "") {
            const tmCount = countMap[String(a.attorney_number.trim())] ?? 0;
            return { ...a, tm_count: tmCount, status: S.SUCCESS };
          }
          return a;
        });
        recalcTotalTM(updated, emptySlots);
        return updated;
      });
      setCurrentStep(1);
    } catch {
      toast.error("Failed to auto-fetch attorney TM counts.");
      setExistingAttorneys((prev) =>
        prev.map((a) => ({ ...a, status: S.IDLE })),
      );
    } finally {
      setAutoFetchingAttorneys(false);
    }
  };

  // ─── Can proceed from attorney step ──────────────────────────────────────
  const canProceedAttorneys = () => {
    // All existing must be verified
    const existingOk = existingAttorneys
      .filter((a) => a.attorney_number.trim() !== "")
      .every((a) => a.status === S.SUCCESS);

    // Any filled-in empty slots must be verified
    const slotsOk = emptySlots
      .filter((s) => s.value.trim() !== "")
      .every((s) => s.status === "valid");

    // At least one attorney total
    const hasAtLeastOne =
      existingAttorneys.filter((a) => a.attorney_number.trim()).length > 0 ||
      emptySlots.filter((s) => s.value.trim() && s.status === "valid").length >
        0;

    // All attorneys with name options must have at least one name selected
    const allNums = [
      ...existingAttorneys.filter((a) => a.attorney_number.trim()).map((a) => a.attorney_number.trim()),
      ...emptySlots.filter((s) => s.value.trim() && s.status === "valid").map((s) => s.value.trim()),
    ];
    const namesOk = allNums.every(
      (num) => !(attorneyNameOptions[num]?.length > 0) || (selectedAttorneyNames[num]?.length > 0),
    );

    return existingOk && slotsOk && hasAtLeastOne && namesOk;
  };

  // ─── Feature handler ─────────────────────────────────────────────────────
  const toggleBoolFeature = (featureId) => {
    const feature = wizardFeatures.find((f) => f.id === featureId);
    const currentlyOn = !!featureStates[`id_${featureId}`];

    setFeatureStates((prev) => {
      const next = { ...prev };
      if (!currentlyOn && feature) {
        // Mutual exclusion: disable siblings with same feature_key
        wizardFeatures.forEach((f) => {
          if (f.feature_key === feature.feature_key && f.id !== featureId) {
            next[`id_${f.id}`] = false;
          }
        });
      }
      next[`id_${featureId}`] = !currentlyOn;
      return next;
    });
  };

  // ─── Step transitions ─────────────────────────────────────────────────────
  const goToBilling = () => {
    const newCalcTM = effectiveTM;
    setCalcTMCount(newCalcTM);
    setCurrentStep(3);
    setBillingPreview(null);
    fetchBillingPreview();
  };

  const validateBillingAndProceed = () => {
    if (planType === "free_trial" || paymentMode === "online") {
      setPaymentErrors({});
      setCurrentStep(4);
      return;
    }

    const errors = {};
    let firstError = null;

    if (!paymentMode) {
      errors.paymentMode = "Payment mode is required.";
      firstError = "Please select a Payment Mode.";
    }
    if ((paymentMode === "bank_transfer" || paymentMode === "upi") && !selectedBank) {
      errors.selectedBank = "Please select a bank.";
      firstError = firstError ?? "Please select a Bank.";
    }
    if (paymentMode === "upi" || paymentMode === "bank_transfer") {
      const trimmedUtr = utrNo.trim();
      if (!trimmedUtr) {
        errors.utrNo = "UTR / Transaction ID is required.";
        firstError = firstError ?? "Please enter the UTR / Transaction ID.";
      } else if (trimmedUtr.length < 12 || trimmedUtr.length > 22) {
        errors.utrNo = "UTR must be between 12 and 22 characters.";
        firstError =
          firstError ?? "UTR / Reference No. must be 12-22 characters.";
      } else if (!/^[a-zA-Z0-9]+$/.test(trimmedUtr)) {
        errors.utrNo = "UTR must contain only alphanumeric characters.";
        firstError =
          firstError ?? "UTR / Reference No. must be alphanumeric only.";
      }
    }
    if (!paymentAttachment) {
      errors.paymentAttachment = "Payment proof is required.";
      firstError =
        firstError ?? "Please upload the Payment Screenshot / Receipt.";
    } else if (paymentAttachment.size > 5 * 1024 * 1024) {
      errors.paymentAttachment = "File size must be ≤ 5 MB.";
      firstError = firstError ?? "Payment proof must be ≤ 5 MB.";
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      toast.error(firstError);
      return;
    }
    setPaymentErrors({});
    setCurrentStep(4);
  };

  // ─── Final save ───────────────────────────────────────────────────────────
  const handleFinalSave = async () => {
    try {
      const formValues = await form.validateFields();

      // Combine existing + new valid attorneys
      const allAttorneys = [
        ...existingAttorneys
          .filter((a) => a.attorney_number.trim())
          .map((a) => ({
            attorney_number: a.attorney_number.trim(),
            tm_count: a.tm_count || null,
            attorney_names: selectedAttorneyNames[a.attorney_number.trim()] || [],
          })),
        ...emptySlots
          .filter((s) => s.status === "valid" && s.value.trim())
          .map((s) => ({
            attorney_number: s.value.trim(),
            tm_count: s.tmCount || null,
            attorney_names: selectedAttorneyNames[s.value.trim()] || [],
          })),
      ];

      /* Checked here as well as on the field: the date lives on step 0, and by
         the time Save is pressed that step is unmounted, so the form's own rule
         no longer runs. The server writes this verbatim — a past date means the
         customer pays for a subscription that is already expired. */
      if (!endDate || !endDate.isAfter(dayjs().startOf("day"))) {
        toast.error("The new subscription end date has to be in the future.");
        setCurrentStep(0);
        return;
      }

      const formData = new FormData();
      formData.append("user_id", subscriptionData?.user_id ?? "");
      formData.append("plan_id", selectedPlanId);
      formData.append("plan_type", planType);
      formData.append(
        "subscription_end_date",
        endDate.format("DD/MM/YYYY"),
      );
      formData.append("plan_price", calcPlanPrice);
      formData.append("total_tm_count", calcTMCount);
      formData.append("total_amount", totalAmount);
      formData.append("gst_inclusion", includeGST ? 1 : 0);
      /* The amount actually shown on screen, at the rate the master gave when
         it was shown — not one the server re-derives later. If the rate changes
         between this screen and the save, the customer is charged what they
         were quoted. */
      formData.append("gst_amount", includeGST ? round2(gstAmount) : 0);

      if (paymentMode === "online") {
        formData.append("payment_status", "2");
      } else {
        formData.append("payment_status", "0");
        const PAYMENT_MODE_LABELS = { upi: "UPI", bank_transfer: "Bank Transfer", cash: "Cash" };
        formData.append("payment_mode", paymentMode ? (PAYMENT_MODE_LABELS[paymentMode] ?? paymentMode) : "");
        formData.append(
          "bank_name",
          (paymentMode === "bank_transfer" || paymentMode === "upi") ? (selectedBank ?? "") : "",
        );
        formData.append(
          "utr_no",
          paymentMode === "upi" || paymentMode === "bank_transfer" ? utrNo : "",
        );
        if (paymentAttachment) {
          formData.append("payment_attachment", paymentAttachment, paymentAttachment.name);
        }
      }

      formData.append("attorney_numbers", JSON.stringify(allAttorneys));

      // Collect enabled feature IDs from wizard features (plan-specific or allFeatures)
      const enabledFeatureIdsList = sortedFeatures
        .filter((f) => {
          const val = featureStates[`id_${f.id}`];
          if (planChanged) {
            return val !== undefined ? !!val : false;
          }
          return val !== undefined ? !!val : existingFeatureIds.includes(f.id);
        })
        .map((f) => f.id);
      formData.append("features", enabledFeatureIdsList.join(","));

      const result = await onUpdateSubscription(formData);
      setWizardOpen(false);

      if (result?.online_payment) {
        const uid = subscriptionData?.user_id;
        setPaymentLink(result.payment_link);
        setOnlineOrderId(result.order_id);
        setPaymentLinkPaid(false);
        localStorage.setItem(
          `online_payment_${uid}`,
          JSON.stringify({ payment_link: result.payment_link, order_id: result.order_id }),
        );
        startPaymentPolling(result.order_id, uid);
        toast.info("Payment link generated! Share it with the customer to complete payment.");
      } else if (result?.pending_approval) {
        setUpgradePending(true);
        toast.info(result.message || "Subscription change submitted for admin approval. It will activate once approved.");
      } else {
        setUpgradePending(false);
        toast.success(result?.message || "Subscription updated successfully!");
      }
    } catch (err) {
      if (err?.errorFields) {
        toast.error("Please fill all required fields in the Plan step.");
        setCurrentStep(0);
      }
    }
  };

  // ── View card display values ───────────────────────────────────────────────
  const gstOn =
    subscriptionData?.gst_inclusion === 1 ||
    subscriptionData?.gst_inclusion === true;
  const attorneyCount = Array.isArray(subscriptionData?.attorney_numbers)
    ? subscriptionData.attorney_numbers.length
    : (subscriptionData?.given_api_count ?? 0);

  // ── Grid styles for attorney rows ─────────────────────────────────────────
  const gridCols = "40px 1fr 120px 110px 160px";
  const headerRowStyle = {
    display: "grid",
    gridTemplateColumns: gridCols,
    padding: "8px 12px",
    background: "#f5f5f5",
    borderRadius: "6px 6px 0 0",
    fontWeight: 600,
    fontSize: 12,
    color: "#595959",
    borderBottom: "1px solid #e8e8e8",
  };
  const rowStyle = (bg) => ({
    display: "grid",
    gridTemplateColumns: gridCols,
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    background: bg,
    alignItems: "center",
    transition: "background 0.2s",
  });

  // ==========================================================================
  // VIEW CARD
  // ==========================================================================
  return (
    <>
      <Card
        title={
          <span>
            <CreditCardOutlined className="me-2" />
            Subscription Details
          </span>
        }
        extra={
          <Space>
            {/* No plan / control / logs yet → the subscription was never set up,
                which is different from an active or a deactivated one. */}
            {subscriptionData?.has_subscription === false ? (
              <Tag
                color="default"
                icon={<ClockCircleOutlined />}
                style={{ fontWeight: 600 }}
              >
                NOT ACTIVE YET
              </Tag>
            ) : (
              <Tag
                color={subscriptionData?.is_active === 1 ? "success" : "error"}
                icon={
                  subscriptionData?.is_active === 1 ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
                style={{ fontWeight: 600 }}
              >
                {subscriptionData?.is_active === 1 ? "ACTIVE" : "INACTIVE"}
              </Tag>
            )}
            {isFreeTrialPlan && (
              <Button
                size="small"
                icon={<CalendarOutlined />}
                onClick={() => setExtendModalOpen(true)}
                style={{ borderColor: "#0891b2", color: "#0891b2" }}
              >
                Extend Free Trial
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              icon={<RocketOutlined />}
              onClick={openWizard}
              loading={isUpdating}
              disabled={Number(subscriptionData?.is_active === 0)}
            >
              Upgrade Subscription
            </Button>
          </Space>
        }
        className="shadow-sm mb-4"
      >
        {upgradePending && (
          <Alert
            type="info"
            showIcon
            message="Upgrade pending approval"
            description="A subscription upgrade has been submitted and is awaiting approval. The current plan remains active until approved."
            className="mb-3"
            style={{ borderRadius: 8 }}
            closable
            onClose={() => setUpgradePending(false)}
          />
        )}
        {paymentLink && !paymentLinkPaid && (
          <Alert
            type="warning"
            showIcon
            message="Online Payment Pending"
            description={
              <div>
                <div style={{ marginBottom: 8 }}>
                  Share this link with the customer to complete payment. The subscription activates automatically once paid.
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fffbe6", padding: "8px 12px", borderRadius: 6, border: "1px solid #ffe58f" }}>
                  <LinkOutlined style={{ color: "#1677ff", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontFamily: "monospace", flex: 1, wordBreak: "break-all", color: "#1677ff" }}>
                    {paymentLink}
                  </span>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(paymentLink);
                      toast.success("Payment link copied!");
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 6 }}>
                  Checking payment status automatically every 10 seconds…
                </div>
              </div>
            }
            className="mb-3"
            style={{ borderRadius: 8 }}
          />
        )}
        {paymentLinkPaid && (
          <Alert
            type="success"
            showIcon
            message="Payment Received — Subscription Active"
            className="mb-3"
            style={{ borderRadius: 8 }}
            closable
            onClose={() => setPaymentLinkPaid(false)}
          />
        )}
        <Descriptions
          column={{ xs: 1, sm: 2, md: 2, lg: 2 }}
          bordered
          size="middle"
        >
          <Descriptions.Item label="Plan Name">
            <strong>{subscriptionData?.plan_name || "N/A"}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="User Type">
            <Tag
              icon={isAttorney ? <UserOutlined /> : <BuildOutlined />}
              color={isAttorney ? "blue" : "purple"}
            >
              {isAttorney ? "Attorney" : "Corporate"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Plan Type">
            <span className="text-capitalize">
              {planTypeLabel(subscriptionData?.plan_type)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Start Date">
            {subscriptionData?.subscription_start_date || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="End Date">
            {subscriptionData?.subscription_end_date || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Plan Price">
            <strong className="text-primary">
              ₹{price(subscriptionData?.plan_price)}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Total Attorneys">
            <span
              style={{
                display: "inline-block",
                minWidth: 36,
                padding: "1px 10px",
                borderRadius: 12,
                background: "#1677ff",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {attorneyCount}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Total TM Count">
            <span
              style={{
                display: "inline-block",
                minWidth: 48,
                padding: "1px 10px",
                borderRadius: 12,
                background: "#52c41a",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {fmt(subscriptionData?.total_tm_count)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <strong className="text-success" style={{ fontSize: 15 }}>
              ₹{fmt(subscriptionData?.total_amount_paid, 2)}
            </strong>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ─── Extend Free Trial Modal ───────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarOutlined style={{ color: "#0891b2" }} />
            <span style={{ fontWeight: 600 }}>Extend Free Trial</span>
          </div>
        }
        open={extendModalOpen}
        onCancel={() => { setExtendModalOpen(false); setExtendDate(null); setExtendReason(""); }}
        footer={null}
        width={480}
        destroyOnClose
      >
        <div style={{ padding: "8px 0" }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              New Expiry Date <span style={{ color: "#ef4444" }}>*</span>
            </div>
            <DatePicker
              style={{ width: "100%" }}
              value={extendDate}
              onChange={(date) => setExtendDate(date)}
              disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
              format="DD/MM/YYYY"
              placeholder="Select new expiry date"
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Extension Reason <span style={{ color: "#ef4444" }}>*</span>
            </div>
            <Input.TextArea
              rows={4}
              placeholder="e.g. User requested additional time recommended by Rahul Das."
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              maxLength={500}
              showCount
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { setExtendModalOpen(false); setExtendDate(null); setExtendReason(""); }}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              loading={isExtending}
              onClick={handleExtendTrial}
              style={{ background: "#0891b2", borderColor: "#0891b2" }}
            >
              Extend Trial
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Wizard Modal ──────────────────────────────────────────────────── */}
      <Modal
        title={
          <div className="d-flex align-items-center gap-2">
            <RocketOutlined style={{ color: "#764ba2" }} />
            <span style={{ fontWeight: 600 }}>Upgrade Subscription</span>
            <Tag
              icon={isAttorney ? <UserOutlined /> : <BuildOutlined />}
              color={isAttorney ? "blue" : "purple"}
            >
              {isAttorney ? "Attorney" : "Corporate"}
            </Tag>
          </div>
        }
        open={wizardOpen}
        onCancel={() => setWizardOpen(false)}
        maskClosable={false}
        footer={null}
        width={920}
        style={{ top: 20 }}
        destroyOnClose
      >
        <Steps
          current={
            isAttorney
              ? currentStep
              : currentStep === 0 ? 0 : currentStep - 1
          }
          size="small"
          className="mb-4"
          items={
            isAttorney
              ? WIZARD_STEPS
              : WIZARD_STEPS.filter((s) => s.title !== "Attorneys")
          }
        />

        {/* ════════════════════════════════════════════════════════════════
            STEP 0 — PLAN
        ════════════════════════════════════════════════════════════════ */}
        {currentStep === 0 && (
          <div>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Select Plan"
                    name="plan_id"
                    rules={[
                      { required: true, message: "Please select a plan" },
                    ]}
                  >
                    <Select
                      placeholder={
                        plansLoading ? "Loading plans…" : "Select plan"
                      }
                      size="large"
                      loading={plansLoading}
                      onChange={handlePlanChange}
                      optionLabelProp="label"
                    >
                      {plans.map((plan) => (
                        <Option key={plan.id} value={plan.id} label={plan.name}>
                          <span>{plan.name}</span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Plan Type"
                    name="plan_type"
                    rules={[
                      { required: true, message: "Please select plan type" },
                    ]}
                  >
                    <Select
                      placeholder="Select type"
                      size="large"
                      onChange={handlePlanTypeChange}
                    >
                      <Option value="monthly">Monthly</Option>
                      <Option value="annual">Annually</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label={<span>Plan Price (per TM)</span>}>
                    <InputNumber
                      size="large"
                      className="w-100"
                      value={planPrice}
                      onChange={(v) => {
                        setPlanPrice(v ?? 0);
                        setCalcPlanPrice(v ?? 0);
                      }}
                      min={0}
                      prefix="₹"
                      formatter={(v) =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(v) => v.replace(/₹\s?|(,*)/g, "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="End Date"
                    name="subscription_end_date"
                    /* A shorter or longer term than the plan type implies is the
                       admin's call — "annual, but only eight months" is a real
                       thing to sell. A date in the past is not: the server
                       applies it as written, so the customer would pay and be
                       expired on the same day. */
                    extra={
                      <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                        When the new term ends — not the current expiry. Change it
                        for a custom term.
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please select end date" },
                      {
                        validator: (_, value) =>
                          (!value || value.isAfter(dayjs().startOf("day"))
                            ? Promise.resolve()
                            : Promise.reject(
                              new Error("The new term has to end in the future"),
                            )),
                      },
                    ]}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      size="large"
                      className="w-100"
                      placeholder="Select end date"
                      disabledDate={(d) => d && d <= dayjs().startOf("day")}
                      onChange={(date) => setEndDate(date)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <div className="d-flex justify-content-end">
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                size="large"
                onClick={fetchAndGoToAttorneys}
                loading={autoFetchingAttorneys}
                style={{ borderRadius: 8, minWidth: 160 }}
              >
                {autoFetchingAttorneys
                  ? "Fetching attorneys…"
                  : isAttorney ? "Next: Attorneys" : "Next: Features"}
              </Button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STEP 1 — ATTORNEYS (Slot-based like AttorneyDetails.jsx)
        ════════════════════════════════════════════════════════════════ */}
        {currentStep === 1 && (
          <div>
            {/* Status tags */}
            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
              {wizardPlanLimit > 0 && (
                <Tag color="purple" icon={<InfoCircleOutlined />}>
                  Plan limit: <strong>{wizardPlanLimit}</strong> attorney
                  {wizardPlanLimit !== 1 ? "s" : ""}
                </Tag>
              )}
              <Tag color="blue">{existingAttorneys.length} existing</Tag>
              <Tag color="green">{totalVerifiedCount} verified</Tag>
              {emptySlots.length > 0 && (
                <Tag color="orange">
                  {emptySlots.length} empty slot
                  {emptySlots.length !== 1 ? "s" : ""}
                </Tag>
              )}
            </div>

            {/* Alerts */}
            {totalPendingCount > 0 && (
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                style={{ borderRadius: 8 }}
                message={
                  <span>
                    <strong>{totalPendingCount}</strong> attorney
                    {totalPendingCount > 1 ? "s" : ""} still need verification
                    before proceeding.
                  </span>
                }
              />
            )}
            {totalPendingCount === 0 &&
              (existingAttorneys.length > 0 ||
                emptySlots.some((s) => s.value.trim())) && (
                <Alert
                  type="success"
                  showIcon
                  className="mb-3"
                  style={{ borderRadius: 8 }}
                  message="All attorneys verified! You can proceed."
                />
              )}

            {/* ── Table header ── */}
            <div style={headerRowStyle}>
              <div>#</div>
              <div>Attorney Number</div>
              <div style={{ textAlign: "center" }}>TM Count</div>
              <div style={{ textAlign: "center" }}>Status</div>
              <div style={{ textAlign: "center" }}>Action</div>
            </div>

            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {/* ── Existing attorneys (LOCKED — read-only, no delete) ── */}
              {existingAttorneys.map((atty, index) => (
                <div key={`ex-${index}`} style={rowStyle("#ffffff")}>
                  {/* # */}
                  <div
                    style={{ color: "#8c8c8c", fontWeight: 600, fontSize: 13 }}
                  >
                    {index + 1}
                  </div>

                  {/* Attorney Number */}
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <FileProtectOutlined style={{ color: "#1677ff" }} />
                      <strong style={{ fontSize: 14 }}>
                        {atty.attorney_number}
                      </strong>
                      <Tooltip title="Existing attorney — cannot be removed">
                        <LockOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
                      </Tooltip>
                    </div>
                    {attorneyNameOptions[atty.attorney_number]?.length > 0 && (() => {
                      const hasSelection = (selectedAttorneyNames[atty.attorney_number]?.length || 0) > 0;
                      return (
                        <div style={{
                          marginTop: 6,
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
                          background: hasSelection ? "#f6ffed" : "#fff1f0",
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: hasSelection ? "#389e0d" : "#cf1322", marginBottom: 4 }}>
                            {hasSelection ? "✓ Name Selected" : "⚠ Select Name:"}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {attorneyNameOptions[atty.attorney_number].map((opt) => {
                              const isSel = (selectedAttorneyNames[atty.attorney_number] || []).includes(opt.attorney_name);
                              return (
                                <div
                                  key={opt.attorney_name}
                                  onClick={() => setSelectedAttorneyNames((prev) => {
                                    const cur = prev[atty.attorney_number] || [];
                                    const exists = cur.includes(opt.attorney_name);
                                    return { ...prev, [atty.attorney_number]: exists ? cur.filter((n) => n !== opt.attorney_name) : [...cur, opt.attorney_name] };
                                  })}
                                  style={{
                                    cursor: "pointer",
                                    padding: "3px 8px",
                                    borderRadius: 5,
                                    fontSize: 11,
                                    fontWeight: isSel ? 600 : 400,
                                    border: `1.5px solid ${isSel ? "#52c41a" : "#d9d9d9"}`,
                                    background: isSel ? "#f6ffed" : "#fafafa",
                                    color: isSel ? "#389e0d" : "#595959",
                                  }}
                                >
                                  {opt.attorney_name}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* TM Count */}
                  <div style={{ textAlign: "center" }}>
                    {atty.status === S.LOADING ? (
                      <Spin size="small" />
                    ) : atty.status === S.SUCCESS ? (
                      <Tag
                        color={atty.tm_count > 0 ? "green" : "default"}
                        style={{ fontWeight: 600, minWidth: 60 }}
                      >
                        {Number(atty.tm_count).toLocaleString("en-IN")}
                      </Tag>
                    ) : (
                      <span style={{ color: "#d9d9d9" }}>—</span>
                    )}
                  </div>

                  {/* Status */}
                  <div style={{ textAlign: "center" }}>
                    {atty.status === S.SUCCESS ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        ACTIVE
                      </Tag>
                    ) : atty.status === S.LOADING ? (
                      <Tag icon={<SyncOutlined spin />} color="processing">
                        CHECKING
                      </Tag>
                    ) : (
                      <Tag color="default">PENDING</Tag>
                    )}
                  </div>

                  {/* Action — locked for existing attorneys */}
                  <div style={{ textAlign: "center" }}>
                    <Button
                      icon={<CheckCircleOutlined />}
                      size="small"
                      disabled
                      style={{
                        fontWeight: 600,
                        borderRadius: 6,
                        cursor: "not-allowed",
                      }}
                    >
                      Active
                    </Button>
                  </div>
                </div>
              ))}

              {/* ── Empty slot rows (editable, check status) ── */}
              {emptySlots.map((slot, index) => {
                const rowNum = existingAttorneys.length + index + 1;
                const isValid = slot.status === "valid";
                const isInvalid = slot.status === "invalid";

                return (
                  <div
                    key={`slot-${index}`}
                    style={rowStyle(
                      isValid ? "#f6ffed" : isInvalid ? "#fff1f0" : "#fafafa",
                    )}
                  >
                    {/* # */}
                    <div
                      style={{
                        color: "#8c8c8c",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {rowNum}
                    </div>

                    {/* Input */}
                    <div>
                      <Input
                        value={slot.value}
                        onChange={(e) =>
                          handleSlotInputChange(index, e.target.value)
                        }
                        placeholder={`Enter attorney number ${rowNum}`}
                        size="middle"
                        maxLength={10}
                        style={{
                          maxWidth: 220,
                          borderColor: isValid
                            ? "#52c41a"
                            : isInvalid
                              ? "#ff4d4f"
                              : undefined,
                          borderWidth: slot.status ? 2 : undefined,
                        }}
                        status={isInvalid ? "error" : undefined}
                        suffix={
                          slot.isChecking ? (
                            <Spin size="small" />
                          ) : isValid ? (
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          ) : isInvalid ? (
                            <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                          ) : (
                            <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
                          )
                        }
                        onPressEnter={() => handleCheckSlotStatus(index)}
                      />
                      {slot.message && (
                        <div style={{ fontSize: 11, marginTop: 3, color: isValid ? "#52c41a" : "#ff4d4f", fontWeight: 500 }}>
                          {isValid ? "✓" : "✗"} {slot.message}
                        </div>
                      )}
                      {isValid && attorneyNameOptions[slot.value.trim()]?.length > 0 && (() => {
                        const num = slot.value.trim();
                        const hasSelection = (selectedAttorneyNames[num]?.length || 0) > 0;
                        return (
                          <div style={{
                            marginTop: 6,
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
                            background: hasSelection ? "#f6ffed" : "#fff1f0",
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: hasSelection ? "#389e0d" : "#cf1322", marginBottom: 4 }}>
                              {hasSelection ? "✓ Name Selected" : "⚠ Select Name:"}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {attorneyNameOptions[num].map((opt) => {
                                const isSel = (selectedAttorneyNames[num] || []).includes(opt.attorney_name);
                                return (
                                  <div
                                    key={opt.attorney_name}
                                    onClick={() => setSelectedAttorneyNames((prev) => {
                                      const cur = prev[num] || [];
                                      const exists = cur.includes(opt.attorney_name);
                                      return { ...prev, [num]: exists ? cur.filter((n) => n !== opt.attorney_name) : [...cur, opt.attorney_name] };
                                    })}
                                    style={{
                                      cursor: "pointer",
                                      padding: "3px 8px",
                                      borderRadius: 5,
                                      fontSize: 11,
                                      fontWeight: isSel ? 600 : 400,
                                      border: `1.5px solid ${isSel ? "#52c41a" : "#d9d9d9"}`,
                                      background: isSel ? "#f6ffed" : "#fafafa",
                                      color: isSel ? "#389e0d" : "#595959",
                                    }}
                                  >
                                    {opt.attorney_name}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* TM Count */}
                    <div style={{ textAlign: "center" }}>
                      {isValid ? (
                        <Tag
                          color="green"
                          style={{ fontWeight: 600, minWidth: 60 }}
                        >
                          {Number(slot.tmCount).toLocaleString("en-IN")}
                        </Tag>
                      ) : (
                        <span style={{ color: "#d9d9d9" }}>—</span>
                      )}
                    </div>

                    {/* Status */}
                    <div style={{ textAlign: "center" }}>
                      {isValid ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                          VALID
                        </Tag>
                      ) : isInvalid ? (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                          INVALID
                        </Tag>
                      ) : (
                        <Tag color="default">EMPTY</Tag>
                      )}
                    </div>

                    {/* Check Status button */}
                    <div style={{ textAlign: "center" }}>
                      <Button
                        icon={<SearchOutlined />}
                        size="small"
                        loading={slot.isChecking}
                        disabled={!slot.value.trim() || slot.isChecking}
                        onClick={() => handleCheckSlotStatus(index)}
                        style={{
                          background: isValid
                            ? "linear-gradient(135deg,#52c41a,#73d13d)"
                            : "linear-gradient(135deg,#722ed1,#9254de)",
                          color: "#fff",
                          border: "none",
                          fontWeight: 600,
                          borderRadius: 6,
                        }}
                      >
                        {isValid ? "Re-check" : "Check Status"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* No slots message */}
            {existingAttorneys.length === 0 && emptySlots.length === 0 && (
              <div className="text-center py-5">
                <TeamOutlined style={{ fontSize: 52, color: "#d9d9d9" }} />
                <p className="text-muted mt-3 mb-0">
                  No attorney slots available for this plan.
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Select a different plan to configure attorneys.
                  </span>
                </p>
              </div>
            )}

            {/* TM stats + override */}
            {(existingAttorneys.length > 0 || emptySlots.length > 0) && (
              <div
                className="mt-3 p-3 rounded"
                style={{
                  background: "linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)",
                  border: "1px solid #bae0ff",
                }}
              >
                <Row gutter={0} align="middle">
                  <Col span={6} className="text-center">
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1677ff",
                      }}
                    >
                      {existingAttorneys.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      Existing
                    </div>
                  </Col>
                  <Col
                    span={6}
                    className="text-center"
                    style={{ borderLeft: "1px solid #bae0ff" }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#722ed1",
                      }}
                    >
                      {emptySlots.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      Empty Slots
                    </div>
                  </Col>
                  <Col
                    span={6}
                    className="text-center"
                    style={{
                      borderLeft: "1px solid #bae0ff",
                      borderRight: "1px solid #bae0ff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#52c41a",
                      }}
                    >
                      {totalTMCount.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      TM Count
                    </div>
                  </Col>
                  <Col span={6} className="text-center">
                    <div
                      style={{
                        fontSize: 12,
                        color: "#8c8c8c",
                        marginBottom: 4,
                      }}
                    >
                      Override TM Count
                    </div>
                    <InputNumber
                      size="small"
                      style={{ width: "90%" }}
                      value={
                        manualTMCount !== null ? manualTMCount : totalTMCount
                      }
                      onChange={(v) => {
                        setManualTMCount(v);
                        setCalcTMCount(v ?? totalTMCount);
                      }}
                      min={0}
                      placeholder="Override"
                    />
                    {manualTMCount !== null &&
                      manualTMCount !== totalTMCount && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "#faad14",
                            cursor: "pointer",
                            marginTop: 2,
                          }}
                          onClick={() => {
                            setManualTMCount(null);
                            setCalcTMCount(totalTMCount);
                          }}
                        >
                          ↩ Reset to ({totalTMCount.toLocaleString("en-IN")})
                        </div>
                      )}
                  </Col>
                </Row>
              </div>
            )}

            <div
              className="d-flex justify-content-between mt-4 pt-3"
              style={{ borderTop: "1px solid #f0f0f0" }}
            >
              <Button
                onClick={() => setCurrentStep(0)}
                style={{ borderRadius: 8 }}
              >
                ← Back
              </Button>
              <Tooltip
                title={
                  !canProceedAttorneys()
                    ? "Verify all attorney numbers first"
                    : ""
                }
              >
                <span>
                  <Button
                    type="primary"
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceedAttorneys()}
                    style={{
                      borderRadius: 8,
                      minWidth: 160,
                      opacity: canProceedAttorneys() ? 1 : 0.6,
                    }}
                    icon={<ArrowRightOutlined />}
                  >
                    Next: Features
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STEP 2 — FEATURES
        ════════════════════════════════════════════════════════════════ */}
        {currentStep === 2 && (
          <div>
            {/* Loading state when fetching plan-specific features */}
            {planChanged && isLoadingPlanFeatures ? (
              <div className="text-center py-5">
                <Spin size="large" />
                <p className="text-muted mt-3">
                  Loading features for the selected plan…
                </p>
              </div>
            ) : sortedFeatures.length > 0 ? (
              <>
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  {selectedPlan && <Tag color="blue">{selectedPlan.name}</Tag>}
                  <Tag color="geekblue" icon={<AppstoreOutlined />}>
                    {sortedFeatures.length} Total
                  </Tag>
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {enabledCount} Enabled
                  </Tag>
                  <Tag color="default">
                    {sortedFeatures.length - enabledCount} Disabled
                  </Tag>
                  <Tag color="gold" icon={<InfoCircleOutlined />}>
                    {planChanged
                      ? "Showing features for the new plan — all enabled by default"
                      : "Existing features pre-enabled — admin can add more"}
                  </Tag>
                </div>

                <div
                  style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}
                >
                  {(() => {
                    const keyGroups = {};
                    sortedFeatures.forEach((f) => {
                      if (!keyGroups[f.feature_key])
                        keyGroups[f.feature_key] = [];
                      keyGroups[f.feature_key].push(f.id);
                    });
                    let lastKey = null;
                    return sortedFeatures.map((feature) => {
                      const stateKey = `id_${feature.id}`;
                      const hasState = stateKey in featureStates;
                      // "Current" tag only shown when plan hasn't changed
                      const isExisting =
                        !planChanged && existingFeatureIds.includes(feature.id);
                      const isOn = hasState
                        ? !!featureStates[stateKey]
                        : planChanged
                          ? false // new plan: default off unless seeded
                          : isExisting;
                      const isGrouped =
                        keyGroups[feature.feature_key].length > 1;
                      const isGroupStart = feature.feature_key !== lastKey;
                      lastKey = feature.feature_key;

                      return (
                        <React.Fragment key={feature.id}>
                          {isGroupStart && isGrouped && (
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#8c8c8c",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                padding: "8px 4px 3px",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <span>
                                {feature.feature_key.replace(/_/g, " ")}
                              </span>
                              <Tag
                                color="orange"
                                style={{
                                  fontSize: 9,
                                  padding: "0 4px",
                                  lineHeight: "14px",
                                }}
                              >
                                Choose one
                              </Tag>
                            </div>
                          )}
                          <div
                            className="d-flex align-items-center justify-content-between mb-2 px-3 py-2 rounded"
                            style={{
                              background: isOn ? "#f6ffed" : "#fafafa",
                              border: `1px solid ${isOn ? "#b7eb8f" : "#f0f0f0"}`,
                              transition: "all 0.2s",
                              cursor: "pointer",
                              marginLeft: isGrouped ? 10 : 0,
                            }}
                            onClick={() => toggleBoolFeature(feature.id)}
                          >
                            <div className="d-flex align-items-start gap-3">
                              <div
                                style={{
                                  marginTop: 2,
                                  flexShrink: 0,
                                  width: 18,
                                  height: 18,
                                  borderRadius: isGrouped ? "50%" : 3,
                                  border: `2px solid ${isOn ? "#52c41a" : "#d9d9d9"}`,
                                  background: isOn ? "#52c41a" : "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {isOn &&
                                  (isGrouped ? (
                                    <div
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: "#fff",
                                      }}
                                    />
                                  ) : (
                                    <svg
                                      width="10"
                                      height="8"
                                      viewBox="0 0 10 8"
                                      fill="none"
                                    >
                                      <path
                                        d="M1 4L3.5 6.5L9 1"
                                        stroke="white"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  ))}
                              </div>
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: isOn ? "#262626" : "#8c8c8c",
                                  }}
                                >
                                  {feature.name}
                                  {isExisting && (
                                    <Tag
                                      color="gold"
                                      style={{ marginLeft: 6, fontSize: 10 }}
                                    >
                                      Current
                                    </Tag>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#8c8c8c",
                                    marginTop: 1,
                                  }}
                                >
                                  {feature.description}
                                </div>
                              </div>
                            </div>
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{ flexShrink: 0, marginLeft: 16 }}
                            >
                              <Switch
                                size="small"
                                checked={isOn}
                                onChange={() => toggleBoolFeature(feature.id)}
                                checkedChildren="ON"
                                unCheckedChildren="OFF"
                                style={{
                                  backgroundColor: isOn ? "#52c41a" : undefined,
                                  minWidth: 46,
                                }}
                              />
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              <Alert
                type="warning"
                showIcon
                message="No features available. Please check the features API."
              />
            )}

            <div
              className="d-flex justify-content-between mt-4 pt-3"
              style={{ borderTop: "1px solid #f0f0f0" }}
            >
              <Button
                onClick={() => setCurrentStep(isAttorney ? 1 : 0)}
                style={{ borderRadius: 8 }}
              >
                ← Back
              </Button>
              <Button
                type="primary"
                onClick={goToBilling}
                style={{ borderRadius: 8, minWidth: 160 }}
                icon={<ArrowRightOutlined />}
              >
                Next: Billing
              </Button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STEP 3 — BILLING
        ════════════════════════════════════════════════════════════════ */}
        {currentStep === 3 && (
          <div>
            {/* ── Amount Calculation ───────────────────────────────────── */}
            <div
              className="p-4 rounded mb-4"
              style={{ background: "#fafafa", border: "1px solid #f0f0f0" }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#262626",
                  marginBottom: 14,
                }}
              >
                <CalculatorOutlined
                  className="me-2"
                  style={{ color: "#1677ff" }}
                />
                Amount Calculation
              </div>

              <Row gutter={24} className="mb-4">
                <Col span={12}>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      marginBottom: 6,
                      fontWeight: 600,
                    }}
                  >
                    TM Count&nbsp;
                    {/* <Tag color="orange" style={{ fontSize: 10 }}>Editable</Tag> */}
                  </div>
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    value={calcTMCount}
                    /* Pinned as a manual override, not just written into the
                       displayed value — otherwise re-checking an attorney runs
                       recalcTotalTM and silently puts the derived count back
                       under a figure the admin had typed. */
                    onChange={(v) => {
                      setCalcTMCount(v ?? 0);
                      setManualTMCount(v ?? 0);
                    }}
                    min={0}
                    formatter={(v) =>
                      `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(v) => v.replace(/(,*)/g, "")}
                  />
                  <div
                    style={{
                      fontSize: 11, color: "#8c8c8c", marginTop: 4,
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <span>Auto-filled · editable</span>
                    {/* Offered only once the figure has actually been moved, and
                        it goes back to the attorneys' own total — the number
                        this box would hold if nobody had touched it. */}
                    {manualTMCount !== null && calcTMCount !== totalTMCount && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setManualTMCount(null);
                          setCalcTMCount(totalTMCount);
                        }}
                        style={{ fontSize: 11, padding: 0, height: "auto", color: "#8c8c8c" }}
                      >
                        Reset to {fmt(totalTMCount)}
                      </Button>
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      marginBottom: 6,
                      fontWeight: 600,
                    }}
                  >
                    Plan Amount (per TM)&nbsp;
                    <Tag color="orange" style={{ fontSize: 10 }}>
                      Editable
                    </Tag>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <InputNumber
                      size="large"
                      style={{ flex: 1 }}
                      /* Typed into a draft, not straight into the price. The box
                         used to write through on every keystroke, so a half-typed
                         "0.0" was already the saved figure and the rest of the
                         screen repriced against it. */
                      value={priceDraft ?? calcPlanPrice}
                      onChange={(v) => setPriceDraft(v ?? 0)}
                      onPressEnter={savePlanPrice}
                      min={0}
                      step={0.01}
                      /* Four places, not two. A large book is priced in fractions
                         of a rupee per trademark — 4,660 marks at ₹0.001 is a real
                         quote — and rounding to paise here turned that into ₹0
                         the moment the field lost focus. */
                      precision={4}
                      decimalSeparator="."
                      prefix="₹"
                      formatter={groupAmount}
                      parser={parseAmount}
                    />
                    {priceDirty && (
                      <Button
                        type="primary"
                        size="large"
                        icon={<CheckOutlined />}
                        onClick={savePlanPrice}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11, color: "#8c8c8c", marginTop: 4,
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <span>
                      {priceDirty
                        ? "Press Save (or Enter) to apply this price"
                        : "Auto-filled from selected plan · decimals allowed"}
                    </span>
                    {(priceDirty || calcPlanPrice !== planPrice) && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setPriceDraft(null);
                          setCalcPlanPrice(planPrice);
                        }}
                        style={{ fontSize: 11, padding: 0, height: "auto", color: "#8c8c8c" }}
                      >
                        Reset to ₹{price(planPrice)}
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Target-total helper: suggests TM counts whose per-TM price is an
                  exact 2-decimal value, so the admin never divides by hand. */}
              {!isFreeTrialType && (
                <div
                  className="rounded p-3 mb-4"
                  style={{ background: "#fafafa", border: "1px solid #f0f0f0" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tooltip title="The negotiated Base Amount before GST. Suggestions below adjust the TM count so the per-TM price divides this total exactly.">
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          borderBottom: "1px dotted #bfbfbf",
                          cursor: "help",
                        }}
                      >
                        Target total (pre-GST)
                      </span>
                    </Tooltip>
                    <InputNumber
                      size="small"
                      value={targetTotal}
                      onChange={(v) => setTargetTotal(v)}
                      min={0}
                      max={100000000}
                      step={0.01}
                      precision={2}
                      decimalSeparator="."
                      placeholder={fmt(baseAmount, 2)}
                      prefix="₹"
                      formatter={groupAmount}
                      parser={parseAmount}
                      style={{ width: 140 }}
                    />
                    {targetTotal != null && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setTargetTotal(null)}
                        style={{ fontSize: 11, padding: 0, color: "#8c8c8c" }}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                  {/* Only once a target total is actually typed. Falling back to
                      the base amount meant that setting a per-TM price — a
                      complete instruction on its own — was answered with a
                      warning that the total it happens to produce is not a round
                      number. A price the admin typed is the price; nothing needs
                      solving unless they ask for a total instead. */}
                  {targetTotal > 0 && (
                    <TmCountSuggestion
                      amount={targetTotal}
                      tmCount={calcTMCount}
                      onSelect={(newCount, perTm) => {
                        // manualTMCount pins the count — otherwise recalcTotalTM
                        // would reset it back to the attorney-derived total.
                        setManualTMCount(newCount);
                        setCalcTMCount(newCount);
                        setCalcPlanPrice(perTm);
                        // The suggestion is the price now; drop any unsaved draft.
                        setPriceDraft(null);
                        setTargetTotal(null);
                      }}
                    />
                  )}
                </div>
              )}

              {/* GST toggle */}
              <div
                className="d-flex align-items-center justify-content-between px-3 py-2 mb-4 rounded"
                style={{
                  background: includeGST ? "#fff7e6" : "#f5f5f5",
                  border: `1px solid ${includeGST ? "#ffd591" : "#f0f0f0"}`,
                  cursor: "pointer",
                }}
                onClick={() => setIncludeGST((v) => !v)}
              >
                <div className="d-flex align-items-center gap-2">
                  <PercentageOutlined
                    style={{ color: includeGST ? "#fa8c16" : "#8c8c8c" }}
                  />
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: includeGST ? "#d46b08" : "#595959",
                    }}
                  >
                    Include GST {gst.ready ? `(${gst.percent}%)` : ""}
                  </span>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={includeGST}
                    onChange={(v) => setIncludeGST(v)}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    style={{
                      backgroundColor: includeGST ? "#fa8c16" : undefined,
                    }}
                  />
                </div>
              </div>

              {/* Breakdown */}
              <div
                className="rounded p-4"
                style={{ background: "#fff", border: "1px solid #e8e8e8" }}
              >
                <div
                  className="d-flex align-items-center justify-content-between mb-3 pb-3"
                  style={{ borderBottom: "1px dashed #f0f0f0" }}
                >
                  <span style={{ fontSize: 13, color: "#8c8c8c" }}>
                    Base Amount ({fmt(calcTMCount)} TMs × ₹{price(calcPlanPrice)})
                  </span>
                  <span
                    style={{ fontWeight: 600, fontSize: 16, color: "#262626" }}
                  >
                    ₹{fmt(baseAmount, 2)}
                  </span>
                </div>
                {includeGST && (
                  <div
                    className="d-flex align-items-center justify-content-between mb-3 pb-3"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <span style={{ fontSize: 13, color: "#8c8c8c" }}>
                      GST {gst.ready ? `(${gst.percent}%)` : ""}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "#fa8c16",
                      }}
                    >
                      + ₹{fmt(gstAmount, 2)}
                    </span>
                  </div>
                )}
                <div className="d-flex align-items-center justify-content-between">
                  <span
                    style={{ fontSize: 15, fontWeight: 700, color: "#262626" }}
                  >
                    Total Amount
                  </span>
                  <span
                    style={{ fontSize: 28, fontWeight: 800, color: "#52c41a" }}
                  >
                    ₹{fmt(totalAmount, 2)}
                  </span>
                </div>

                {/* ── Billing preview: proration + wallet deduction (live) ── */}
                {billingPreviewLoading && (
                  <div style={{ borderTop: "1px dashed #f0f0f0", marginTop: 12, paddingTop: 10, textAlign: "center" }}>
                    <Spin size="small" />
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#8c8c8c" }}>Calculating proration & wallet…</span>
                  </div>
                )}
                {!billingPreviewLoading && billingPreview && !isFreeTrialType && (
                  <>
                    {prorationCredit > 0 && (
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ borderTop: "1px dashed #f0f0f0", marginTop: 10, paddingTop: 10 }}
                      >
                        <span style={{ fontSize: 13, color: "#8c8c8c" }}>
                          <WalletOutlined className="me-1" style={{ color: "#d97706" }} />
                          Proration Credit (remaining days)
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 15, color: "#d97706" }}>
                          − ₹{fmt(prorationCredit, 2)}
                        </span>
                      </div>
                    )}
                    {liveWalletDeduction > 0 && (
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ borderTop: "1px dashed #f0f0f0", marginTop: 10, paddingTop: 10 }}
                      >
                        <span style={{ fontSize: 13, color: "#8c8c8c" }}>
                          <WalletOutlined className="me-1" style={{ color: "#16a34a" }} />
                          Wallet Deduction (balance: ₹{fmt(walletBalance, 2)})
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 15, color: "#16a34a" }}>
                          − ₹{fmt(liveWalletDeduction, 2)}
                        </span>
                      </div>
                    )}
                    {(prorationCredit > 0 || liveWalletDeduction > 0) && (
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ borderTop: "2px solid #e8e8e8", marginTop: 10, paddingTop: 12 }}
                      >
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#1d3461" }}>
                          Net Amount to Pay
                        </span>
                        <span style={{ fontSize: 26, fontWeight: 800, color: "#1677ff" }}>
                          ₹{fmt(netAmountToPay, 2)}
                        </span>
                      </div>
                    )}
                    {endDate && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#8c8c8c" }}>
                        New subscription end date: <strong style={{ color: "#374151" }}>{endDate.format("DD/MM/YYYY")}</strong>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Payment Details ───────────────────────────────────────── */}
            {isFreeTrialType ? (
              <div
                className="p-4 rounded d-flex align-items-center gap-3"
                style={{ background: "#e6f7ff", border: "1px solid #91caff" }}
              >
                <span style={{ fontSize: 22 }}>🎁</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0958d9" }}>
                    Free Trial — No Payment Required
                  </div>
                  <div style={{ fontSize: 12, color: "#4096ff", marginTop: 2 }}>
                    Billing amount is ₹0.00. You can proceed directly to review.
                  </div>
                </div>
              </div>
            ) : (
            <div
              className="p-4 rounded"
              style={{ background: "#fafafa", border: "1px solid #f0f0f0" }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#262626",
                  marginBottom: 14,
                }}
              >
                <CreditCardOutlined
                  className="me-2"
                  style={{ color: "#1677ff" }}
                />
                Payment Details
              </div>

              <Row gutter={16} className="mb-3">
                <Col span={12}>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      marginBottom: 6,
                      fontWeight: 600,
                    }}
                  >
                    Payment Mode <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <Select
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Select payment mode"
                    value={paymentMode}
                    status={paymentErrors.paymentMode ? "error" : ""}
                    onChange={(v) => {
                      setPaymentMode(v);
                      setSelectedBank(null);
                      setUtrNo("");
                      setPaymentAttachment(null);
                      setPaymentErrors((prev) => ({
                        ...prev,
                        paymentMode: null,
                      }));
                    }}
                  >
                    <Option value="online">🔗 Send Online Payment Link</Option>
                    <Option value="upi">📱 UPI</Option>
                    <Option value="bank_transfer">🏦 Bank Transfer</Option>
                    <Option value="cash">💵 Cash</Option>
                  </Select>
                  {paymentErrors.paymentMode && (
                    <div
                      style={{ color: "#ff4d4f", fontSize: 11, marginTop: 4 }}
                    >
                      ⚠ {paymentErrors.paymentMode}
                    </div>
                  )}
                </Col>
              </Row>

              {paymentMode === "online" && (
                <div
                  className="p-3 rounded mt-2"
                  style={{ background: "#f0f5ff", border: "1px solid #adc6ff" }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#2f54eb", marginBottom: 6 }}>
                    🔗 Online Payment Link
                  </div>
                  <div style={{ fontSize: 12, color: "#595959" }}>
                    A secure payment link will be generated after saving. Share it with the customer — once they pay, the subscription activates automatically.
                  </div>
                </div>
              )}

              {paymentMode && paymentMode !== "online" && (
                <div
                  className="p-3 rounded mt-2"
                  style={{
                    background: "#fff",
                    border: `1px solid ${
                      paymentMode === "upi"
                        ? "#b5f5ec"
                        : paymentMode === "bank_transfer"
                          ? "#bae0ff"
                          : "#d9f7be"
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      marginBottom: 12,
                      color:
                        paymentMode === "upi"
                          ? "#13c2c2"
                          : paymentMode === "bank_transfer"
                            ? "#1677ff"
                            : "#52c41a",
                    }}
                  >
                    {paymentMode === "upi" && "📱 UPI Payment Details"}
                    {paymentMode === "bank_transfer" &&
                      "🏦 Bank Transfer Details"}
                    {paymentMode === "cash" && "💵 Cash Payment Details"}
                  </div>

                  <Row gutter={16}>
                    {(paymentMode === "bank_transfer" || paymentMode === "upi") && (
                      <Col span={12} className="mb-3">
                        <div
                          style={{
                            fontSize: 12,
                            color: "#8c8c8c",
                            marginBottom: 6,
                            fontWeight: 600,
                          }}
                        >
                          Bank Name{" "}
                          <span style={{ color: "#ff4d4f" }}>*</span>
                        </div>
                        <Select
                          size="middle"
                          style={{ width: "100%" }}
                          placeholder="Choose bank"
                          value={selectedBank}
                          loading={banksLoading}
                          status={paymentErrors.selectedBank ? "error" : ""}
                          onChange={(v) => {
                            setSelectedBank(v);
                            setPaymentErrors((prev) => ({
                              ...prev,
                              selectedBank: null,
                            }));
                          }}
                        >
                          {bankData.map((bank) => (
                            <Option key={bank.value} value={bank.value}>
                              {bank.label}
                            </Option>
                          ))}
                        </Select>
                        {paymentErrors.selectedBank && (
                          <div
                            style={{
                              color: "#ff4d4f",
                              fontSize: 11,
                              marginTop: 4,
                            }}
                          >
                            ⚠ {paymentErrors.selectedBank}
                          </div>
                        )}
                      </Col>
                    )}

                    {(paymentMode === "upi" ||
                      paymentMode === "bank_transfer") && (
                      <Col
                        span={paymentMode === "bank_transfer" ? 12 : 24}
                        className="mb-3"
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#8c8c8c",
                            marginBottom: 6,
                            fontWeight: 600,
                          }}
                        >
                          UTR / Reference No.{" "}
                          <span style={{ color: "#ff4d4f" }}>*</span>
                        </div>
                        <Input
                          size="middle"
                          placeholder={
                            paymentMode === "upi"
                              ? "Enter UPI transaction ID / UTR"
                              : "Enter UTR number (12-22 alphanumeric characters)"
                          }
                          value={utrNo}
                          maxLength={22}
                          status={paymentErrors.utrNo ? "error" : ""}
                          onChange={(e) => {
                            // Allow only alphanumeric characters
                            const cleaned = e.target.value.replace(
                              /[^a-zA-Z0-9]/g,
                              "",
                            );
                            setUtrNo(cleaned);
                            setPaymentErrors((prev) => ({
                              ...prev,
                              utrNo: null,
                            }));
                          }}
                          style={{ borderRadius: 6 }}
                          prefix={
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                              #
                            </span>
                          }
                          suffix={
                            utrNo.trim() ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color:
                                    utrNo.trim().length >= 12 &&
                                    utrNo.trim().length <= 22
                                      ? "#52c41a"
                                      : "#ff4d4f",
                                }}
                              >
                                {utrNo.trim().length}/22
                              </span>
                            ) : null
                          }
                        />
                        {paymentErrors.utrNo && (
                          <div
                            style={{
                              color: "#ff4d4f",
                              fontSize: 11,
                              marginTop: 4,
                            }}
                          >
                            ⚠ {paymentErrors.utrNo}
                          </div>
                        )}
                      </Col>
                    )}

                    <Col span={24}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        {paymentMode === "cash"
                          ? "Cash Receipt / Proof"
                          : "Payment Screenshot / Receipt"}
                        &nbsp;<span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <div
                        style={{
                          border: `1px dashed ${
                            paymentErrors.paymentAttachment
                              ? "#ff4d4f"
                              : paymentAttachment
                                ? "#52c41a"
                                : "#d9d9d9"
                          }`,
                          borderRadius: 8,
                          padding: "12px 16px",
                          background: paymentErrors.paymentAttachment
                            ? "#fff2f0"
                            : paymentAttachment
                              ? "#f6ffed"
                              : "#fafafa",
                          cursor: "pointer",
                          textAlign: "center",
                          transition: "all 0.2s",
                        }}
                        onClick={() =>
                          document
                            .getElementById("payment-attachment-input")
                            .click()
                        }
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.warning("File must be ≤ 5 MB.");
                              return;
                            }
                            setPaymentAttachment(file);
                            setPaymentErrors((prev) => ({
                              ...prev,
                              paymentAttachment: null,
                            }));
                          }
                        }}
                      >
                        <input
                          id="payment-attachment-input"
                          type="file"
                          accept="image/*,application/pdf"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast.warning("File must be ≤ 5 MB.");
                                e.target.value = "";
                                return;
                              }
                              setPaymentAttachment(file);
                              setPaymentErrors((prev) => ({
                                ...prev,
                                paymentAttachment: null,
                              }));
                            }
                          }}
                        />
                        {paymentAttachment ? (
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 16 }}
                            />
                            <span
                              style={{
                                fontSize: 13,
                                color: "#262626",
                                fontWeight: 600,
                              }}
                            >
                              {paymentAttachment.name}
                            </span>
                            <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                              ({(paymentAttachment.size / 1024).toFixed(1)} KB)
                            </span>
                            <Button
                              size="small"
                              danger
                              type="text"
                              icon={<CloseCircleOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPaymentAttachment(null);
                              }}
                              style={{ fontSize: 11 }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 22, marginBottom: 4 }}>
                              📎
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: paymentErrors.paymentAttachment
                                  ? "#ff4d4f"
                                  : "#8c8c8c",
                              }}
                            >
                              Click or drag & drop (JPG, PNG, PDF)
                            </div>
                          </div>
                        )}
                      </div>
                      {paymentErrors.paymentAttachment && (
                        <div
                          style={{
                            color: "#ff4d4f",
                            fontSize: 11,
                            marginTop: 4,
                          }}
                        >
                          ⚠ {paymentErrors.paymentAttachment}
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </div>
            )}

            <div
              className="d-flex justify-content-between mt-4 pt-3"
              style={{ borderTop: "1px solid #f0f0f0" }}
            >
              <Button
                onClick={() => setCurrentStep(2)}
                style={{ borderRadius: 8 }}
              >
                ← Back
              </Button>
              <Button
                type="primary"
                onClick={validateBillingAndProceed}
                style={{ borderRadius: 8, minWidth: 160 }}
                icon={<ArrowRightOutlined />}
              >
                Review & Save
              </Button>
            </div>
          </div>
        )}


        {/* ════════════════════════════════════════════════════════════════
            STEP 4 — REVIEW
        ════════════════════════════════════════════════════════════════ */}
        {currentStep === 4 && (
          <div>
            <Alert
              type="info"
              showIcon
              className="mb-3"
              style={{ borderRadius: 8 }}
              message="Review all changes carefully before saving."
            />

            {/* Plan */}
            <Card
              size="small"
              title={
                <span>
                  <CreditCardOutlined className="me-2 text-primary" />
                  Plan
                </span>
              }
              className="mb-3"
              style={{ borderRadius: 8 }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Plan"
                    value={
                      selectedPlan?.name ?? subscriptionData?.plan_name ?? "—"
                    }
                    valueStyle={{ fontSize: 13, fontWeight: 600 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Plan Price (per TM)"
                    value={calcPlanPrice}
                    prefix="₹"
                    formatter={(v) => fmt(v, 2)}
                    valueStyle={{ fontSize: 13, color: "#1677ff" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Total Amount"
                    value={totalAmount}
                    prefix="₹"
                    formatter={(v) => fmt(v, 2)}
                    valueStyle={{
                      fontSize: 13,
                      color: "#52c41a",
                      fontWeight: 700,
                    }}
                  />
                </Col>
              </Row>
              <div
                className="mt-3 p-2 rounded"
                style={{
                  background: "#f9f9f9",
                  border: "1px solid #f0f0f0",
                  fontSize: 12,
                  color: "#8c8c8c",
                }}
              >
                {fmt(calcTMCount)} TMs × ₹{price(calcPlanPrice)} = ₹
                {fmt(baseAmount, 2)}
                {includeGST && (
                  <span>
                    &nbsp;+ GST {gst.percent}% (₹{fmt(gstAmount, 2)}) =&nbsp;
                    <strong style={{ color: "#52c41a" }}>
                      ₹{fmt(totalAmount, 2)}
                    </strong>
                  </span>
                )}
              </div>

              {/* Proration + wallet breakdown (mirrors billing step) */}
              {billingPreview && !isFreeTrialType && (prorationCredit > 0 || liveWalletDeduction > 0) && (
                <div
                  className="mt-3 p-2 rounded"
                  style={{ background: "#f0f5ff", border: "1px solid #adc6ff", fontSize: 12 }}
                >
                  {prorationCredit > 0 && (
                    <div className="d-flex justify-content-between" style={{ marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>
                        <WalletOutlined className="me-1" style={{ color: "#d97706" }} />
                        Proration Credit
                      </span>
                      <span style={{ fontWeight: 600, color: "#d97706" }}>− ₹{fmt(prorationCredit, 2)}</span>
                    </div>
                  )}
                  {liveWalletDeduction > 0 && (
                    <div className="d-flex justify-content-between" style={{ marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>
                        <WalletOutlined className="me-1" style={{ color: "#16a34a" }} />
                        Wallet Deduction (bal: ₹{fmt(walletBalance, 2)})
                      </span>
                      <span style={{ fontWeight: 600, color: "#16a34a" }}>− ₹{fmt(liveWalletDeduction, 2)}</span>
                    </div>
                  )}
                  <div
                    className="d-flex justify-content-between"
                    style={{ borderTop: "1px solid #adc6ff", paddingTop: 6, marginTop: 4 }}
                  >
                    <span style={{ fontWeight: 700, color: "#1d3461" }}>Net Amount to Pay</span>
                    <span style={{ fontWeight: 800, fontSize: 14, color: "#1677ff" }}>₹{fmt(netAmountToPay, 2)}</span>
                  </div>
                </div>
              )}
            </Card>

            {/* Attorneys */}
            <Card
              size="small"
              title={
                <span>
                  <TeamOutlined className="me-2 text-primary" />
                  Attorneys
                </span>
              }
              className="mb-3"
              style={{ borderRadius: 8 }}
            >
              <Row gutter={16} className="mb-2">
                <Col span={8}>
                  <Statistic
                    title="Existing"
                    value={existingAttorneys.length}
                    valueStyle={{ color: "#1677ff", fontSize: 20 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="New"
                    value={verifiedNewCount}
                    valueStyle={{ color: "#722ed1", fontSize: 20 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={
                      manualTMCount !== null
                        ? "TM Count (Override)"
                        : "TM Count"
                    }
                    value={calcTMCount}
                    formatter={(v) => fmt(v)}
                    valueStyle={{ color: "#52c41a", fontSize: 20 }}
                  />
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-1 mt-2">
                {existingAttorneys
                  .filter((a) => a.attorney_number.trim())
                  .map((a, i) => (
                    <Tag
                      key={`rev-ex-${i}`}
                      icon={<LockOutlined />}
                      color="blue"
                      style={{ fontSize: 11 }}
                    >
                      {a.attorney_number}
                      {a.tm_count > 0 && (
                        <span style={{ marginLeft: 4, opacity: 0.8 }}>
                          ({a.tm_count.toLocaleString("en-IN")} TMs)
                        </span>
                      )}
                    </Tag>
                  ))}
                {emptySlots
                  .filter((s) => s.status === "valid" && s.value.trim())
                  .map((s, i) => (
                    <Tag
                      key={`rev-new-${i}`}
                      icon={<FileProtectOutlined />}
                      color="purple"
                      style={{ fontSize: 11 }}
                    >
                      {s.value.trim()}
                      {s.tmCount > 0 && (
                        <span style={{ marginLeft: 4, opacity: 0.8 }}>
                          ({s.tmCount.toLocaleString("en-IN")} TMs)
                        </span>
                      )}
                      <Tag
                        color="green"
                        style={{
                          marginLeft: 4,
                          fontSize: 9,
                          padding: "0 3px",
                          lineHeight: "14px",
                        }}
                      >
                        NEW
                      </Tag>
                    </Tag>
                  ))}
              </div>
            </Card>

            {/* Features */}
            <Card
              size="small"
              title={
                <span>
                  <AppstoreOutlined className="me-2 text-primary" />
                  Features
                </span>
              }
              className="mb-3"
              style={{ borderRadius: 8 }}
            >
              <div
                className="d-flex flex-wrap gap-1"
                style={{ maxHeight: 120, overflowY: "auto" }}
              >
                {sortedFeatures.map((f) => {
                  const val = featureStates[`id_${f.id}`];
                  const isOn =
                    val !== undefined
                      ? !!val
                      : planChanged
                        ? false
                        : existingFeatureIds.includes(f.id);
                  if (!isOn) return null;
                  return (
                    <Tag
                      key={f.id}
                      icon={<CheckCircleOutlined />}
                      color="success"
                      style={{ fontSize: 10, marginBottom: 2 }}
                    >
                      {f.name.length > 34 ? f.name.slice(0, 34) + "…" : f.name}
                    </Tag>
                  );
                })}
              </div>
            </Card>

            {/* Payment */}
            <Card
              size="small"
              title={
                <span>
                  <CreditCardOutlined className="me-2 text-primary" />
                  Payment
                </span>
              }
              className="mb-3"
              style={{ borderRadius: 8 }}
            >
              <Row gutter={16} align="middle" style={{ flexWrap: "wrap" }}>
                <Col span={paymentMode === "online" ? 24 : 6}>
                  <div
                    style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}
                  >
                    Mode
                  </div>
                  {paymentMode === "online" ? (
                    <div>
                      <Tag color="blue" style={{ fontSize: 12 }}>🔗 Online Payment Link</Tag>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>
                        A payment link will be generated after saving. The subscription activates once the customer pays.
                      </div>
                    </div>
                  ) : (
                    <Tag color="blue" style={{ fontSize: 12 }}>
                      {paymentMode === "upi"
                        ? "📱 UPI"
                        : paymentMode === "bank_transfer"
                          ? "🏦 Bank Transfer"
                          : paymentMode === "cash"
                            ? "💵 Cash"
                            : "—"}
                    </Tag>
                  )}
                </Col>
                {paymentMode === "bank_transfer" && selectedBank && (
                  <Col span={6}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#8c8c8c",
                        marginBottom: 4,
                      }}
                    >
                      Bank
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {selectedBank}
                    </div>
                  </Col>
                )}
                {(paymentMode === "upi" || paymentMode === "bank_transfer") &&
                  utrNo && (
                    <Col span={6}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          marginBottom: 4,
                        }}
                      >
                        UTR / Ref
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1677ff",
                          fontFamily: "monospace",
                        }}
                      >
                        {utrNo}
                      </div>
                    </Col>
                  )}
                {paymentAttachment && (
                  <Col span={6}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#8c8c8c",
                        marginBottom: 4,
                      }}
                    >
                      Attachment
                    </div>
                    <Tag
                      color="green"
                      icon={<CheckCircleOutlined />}
                      style={{ fontSize: 11 }}
                    >
                      {paymentAttachment.name.length > 18
                        ? paymentAttachment.name.slice(0, 18) + "…"
                        : paymentAttachment.name}
                    </Tag>
                  </Col>
                )}
              </Row>
            </Card>

            <div
              className="d-flex justify-content-between mt-4 pt-3"
              style={{ borderTop: "1px solid #f0f0f0" }}
            >
              <Button
                onClick={() => setCurrentStep(3)}
                style={{ borderRadius: 8 }}
              >
                ← Back
              </Button>
              <Space>
                <Button
                  onClick={() => setWizardOpen(false)}
                  style={{ borderRadius: 8 }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleFinalSave}
                  loading={isUpdating}
                  style={{ borderRadius: 8, minWidth: 160 }}
                  icon={<CheckCircleOutlined />}
                >
                  Save All Changes
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SubscriptionDetails;
